/**
 * Unit parsing and conversion.
 *
 * Rule for this whole module: an unrecognised unit returns `null`. We never
 * fall back to "probably grams" — a wrong basis silently corrupts every
 * downstream metric (section 8: "Never guess").
 */

import type { QuantityKind, QuantityUnit } from '@/types/product';

/** Grams per one of each mass unit. */
const MASS_TO_G: Record<string, number> = {
  mg: 0.001,
  g: 1,
  gram: 1,
  grams: 1,
  gm: 1,
  kg: 1000,
  kilo: 1000,
  kilos: 1000,
  kilogram: 1000,
  kilograms: 1000,
  oz: 28.349523125,
  ounce: 28.349523125,
  ounces: 28.349523125,
  lb: 453.59237,
  lbs: 453.59237,
  pound: 453.59237,
  pounds: 453.59237,
};

/** Millilitres per one of each volume unit. */
const VOLUME_TO_ML: Record<string, number> = {
  ml: 1,
  milliliter: 1,
  millilitre: 1,
  milliliters: 1,
  millilitres: 1,
  cl: 10,
  dl: 100,
  l: 1000,
  lt: 1000,
  ltr: 1000,
  liter: 1000,
  litre: 1000,
  liters: 1000,
  litres: 1000,
  'fl oz': 29.5735295625,
  floz: 29.5735295625,
  'fl.oz': 29.5735295625,
};

const COUNT_UNITS = new Set([
  'piece',
  'pieces',
  'pc',
  'pcs',
  'ct',
  'count',
  'pack',
  'packs',
  'sachet',
  'sachets',
  'bar',
  'bars',
  'egg',
  'eggs',
  'slice',
  'slices',
  'tablet',
  'tablets',
  'capsule',
  'capsules',
  'serving',
  'servings',
]);

/** Canonical unit symbol for each kind. */
export const CANONICAL_UNIT: Record<QuantityKind, QuantityUnit> = {
  mass: 'g',
  volume: 'ml',
  count: 'piece',
};

function normaliseToken(raw: string): string {
  return raw.trim().toLowerCase().replace(/\./g, '.').replace(/\s+/g, ' ');
}

/** Which physical dimension a unit token belongs to, or `null` if unknown. */
export function unitKind(raw: string): QuantityKind | null {
  const u = normaliseToken(raw);
  if (u in MASS_TO_G) return 'mass';
  if (u in VOLUME_TO_ML) return 'volume';
  if (COUNT_UNITS.has(u)) return 'count';
  return null;
}

/**
 * Convert `value` of `unit` into the canonical unit for its kind
 * (mass -> g, volume -> ml, count -> pieces).
 *
 * Returns `null` for unknown units, non-finite values, or negative values.
 * Zero is preserved: a genuine 0 is data, not absence.
 */
export function toCanonical(
  value: number,
  unit: string,
): { amount: number; kind: QuantityKind } | null {
  if (!Number.isFinite(value) || value < 0) return null;
  const u = normaliseToken(unit);

  const mass = MASS_TO_G[u];
  if (mass !== undefined) return { amount: value * mass, kind: 'mass' };

  const vol = VOLUME_TO_ML[u];
  if (vol !== undefined) return { amount: value * vol, kind: 'volume' };

  if (COUNT_UNITS.has(u)) return { amount: value, kind: 'count' };

  return null;
}

/**
 * Convert between two units of the *same* kind. Cross-kind conversion (g -> ml)
 * is refused: it needs a density we do not have.
 */
export function convert(value: number, from: string, to: string): number | null {
  const a = toCanonical(value, from);
  if (a === null) return null;
  const b = toCanonical(1, to);
  if (b === null || b.kind !== a.kind) return null;
  if (b.amount === 0) return null;
  return a.amount / b.amount;
}

/** Canonicalise a free-text unit into our `QuantityUnit` vocabulary. */
export function canonicalUnitOf(raw: string): QuantityUnit | null {
  const kind = unitKind(raw);
  return kind === null ? null : CANONICAL_UNIT[kind];
}

/**
 * Format a canonical amount for display, promoting to the larger unit when
 * that reads better (1500 g -> "1.5 kg").
 */
export function formatQuantity(amount: number, kind: QuantityKind): string {
  if (!Number.isFinite(amount)) return 'N/A';
  if (kind === 'count') {
    return `${trimNumber(amount)} ${amount === 1 ? 'piece' : 'pieces'}`;
  }
  if (kind === 'mass') {
    return amount >= 1000 ? `${trimNumber(amount / 1000)} kg` : `${trimNumber(amount)} g`;
  }
  return amount >= 1000 ? `${trimNumber(amount / 1000)} L` : `${trimNumber(amount)} ml`;
}

/**
 * Render a number without inventing precision (section 9). At most 2 decimals,
 * and trailing zeros are dropped.
 */
export function trimNumber(n: number, maxDecimals = 2): string {
  if (!Number.isFinite(n)) return 'N/A';
  const rounded = Number(n.toFixed(maxDecimals));
  return String(rounded);
}
